import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FogbugzToAdoCreateBody, FogbugzToAdoReporterQuestion } from "@ttc-spectree/toolbox-contract";
import { fogbugzKeys } from "@/hooks/queries/use-fogbugz";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import { fogbugzToAdoApi, type FogbugzToAdoBarCheckBody } from "@/lib/api/fogbugz-to-ado";

/**
 * TanStack Query hooks for FogBugz to ADO (epic PI-SPEC-SW-E311, feature
 * PI-SPEC-SW-1640). Every write is a mutation the page fires only after the
 * PM's explicit click; nothing retries a write.
 */

export const fogbugzToAdoKeys = {
  all: ["fogbugz-to-ado"] as const,
  classification: (project: string) => [...fogbugzToAdoKeys.all, "classification", project] as const,
  /** The create mutation, so the page can tell a create is in flight (`useIsMutating`). */
  create: () => [...fogbugzToAdoKeys.all, "create-bug"] as const,
};

/** Read a case as the PM (bundle or refusal). */
export function usePrepareFogbugzCase() {
  return useMutation({
    mutationFn: (caseId: number) => fogbugzToAdoApi.prepare(caseId),
  });
}

/** The Bug bar on the confirmed slots and the PM's additions. */
export function useFogbugzToAdoBarCheck() {
  return useMutation({
    mutationFn: ({ caseId, body }: { caseId: number; body: FogbugzToAdoBarCheckBody }) =>
      fogbugzToAdoApi.barCheck(caseId, body),
  });
}

/** Post the reviewed reporter questions as one FogBugz note; the case's events are refetched. */
export function usePostReporterQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, questions }: { caseId: number; questions: FogbugzToAdoReporterQuestion[] }) =>
      fogbugzToAdoApi.postReporterQuestions(caseId, questions),
    onSuccess: (_result, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(caseId) });
    },
  });
}

/** The ADO project's area paths and iterations for the pickers. */
export function useFogbugzToAdoClassification(project: string | null) {
  return useQuery({
    queryKey: fogbugzToAdoKeys.classification(project ?? ""),
    queryFn: () => fogbugzToAdoApi.classification(project ?? ""),
    enabled: project !== null && project !== "",
    staleTime: 5 * 60_000,
  });
}

/**
 * Create the reviewed Bug; the case's events are refetched (the note was posted
 * to it) and so is the ADO project's backlog, where the new Bug now sits.
 */
export function useCreateFogbugzToAdoBug() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: fogbugzToAdoKeys.create(),
    mutationFn: ({ caseId, body }: { caseId: number; project: string; body: FogbugzToAdoCreateBody }) =>
      fogbugzToAdoApi.createBug(caseId, body),
    onSuccess: (_result, { caseId, project }) => {
      void queryClient.invalidateQueries({ queryKey: fogbugzKeys.case(caseId) });
      void queryClient.invalidateQueries({ queryKey: [...groomingKeys.project(project), "backlog"] });
    },
  });
}

/** True while a Bug is being created (the page keeps the case box waiting until it ends). */
export function useCreatingFogbugzToAdoBug(): boolean {
  return useIsMutating({ mutationKey: fogbugzToAdoKeys.create() }) > 0;
}
